import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  useTheme,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ChatIcon from "@mui/icons-material/Chat";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import PageTransition from "../components/PageTransition";

function NotFoundPage() {
  const theme = useTheme();

  return (
    <PageTransition>
      <Helmet>
        <title>Page Not Found – David Kooi</title>
        <meta name="robots" content="noindex" />
        <meta name="description" content="The page you're looking for doesn't exist." />
      </Helmet>
      
      <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.background.default }}>
        <Navigation />

        {/* Landmark for the header skip link; content is not re-indented
            so the diff stays two lines. */}
        <Box component="main" id="main">
        
        <Box
          sx={{
            minHeight: "calc(100vh - 64px - 120px)",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
          }}
        >
          <Container maxWidth="sm">
            <Box
              sx={{
                textAlign: "center",
                color: "white",
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "6rem", md: "8rem" },
                  fontWeight: 800,
                  lineHeight: 1,
                  mb: 2,
                  textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                }}
              >
                404
              </Typography>
              
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  textShadow: "0 2px 10px rgba(0,0,0,0.2)",
                }}
              >
                Looks like I wandered off the trail...
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  maxWidth: 400,
                  mx: "auto",
                }}
              >
                The page you're looking for doesn't exist or has been moved. 
                But hey, getting lost sometimes leads to the best discoveries.
              </Typography>
              
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
              >
                <Button
                  component={RouterLink}
                  to="/"
                  variant="contained"
                  size="large"
                  startIcon={<HomeIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    backgroundColor: "white",
                    color: "#667eea",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.9)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Back to Home
                </Button>
                
                <Button
                  component={RouterLink}
                  to="/chat"
                  variant="outlined"
                  size="large"
                  startIcon={<ChatIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderColor: "white",
                    color: "white",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: "rgba(255,255,255,0.1)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Chat with ChatDDK
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>
        
        </Box>

        <Footer />
      </Box>
    </PageTransition>
  );
}

export default NotFoundPage;
